/**
 * TUTTI Wishlist — Card-level heart buttons & header count
 *
 * Works alongside the Gadget "Product Wishlists" app which handles:
 *   - PDP wishlist button (app block)
 *   - Customer account wishlist UI extension
 *   - Data persistence
 *
 * This script handles:
 *   - Heart icons on product cards (collection, search, featured)
 *   - Wishlist count in header
 *   - localStorage cache for instant UI state
 *   - Syncing state with Gadget API via app proxy
 *
 * Gadget API notes:
 *   - Uses the Gadget JS client (web.min.js) loaded from CDN
 *   - Models: wishlist (has many wishlistItems), wishlistItem (belongs to wishlist, has variant)
 *   - Variants are Shopify variant IDs linked via _link
 *   - Wishlists are scoped per-customer via Gadget's tenancy
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'tutti_wishlist';
  const PROXY_PATH = window.wishlistConfig?.proxyPath || '/apps/dbxgfg0h7yrls';
  const GQL_ENDPOINT = PROXY_PATH + '/api/graphql';

  /* --------------------------------------------------------------------------
     State
     -------------------------------------------------------------------------- */
  // Set of product handles that are wishlisted (for card UI)
  let wishlistedHandles = new Set();
  // Map of handle → { wishlistId, wishlistItemId, variantId } for removal
  let wishlistLookup = {};
  // The customer's default (first) wishlist ID 
  let defaultWishlistId = null;
  // Gadget API client instance
  let api = null;
  let isFetching = false;
  let isApiReady = false;

  /* --------------------------------------------------------------------------
     localStorage Cache (instant UI for all users)
     -------------------------------------------------------------------------- */
  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...wishlistedHandles]));
    } catch (e) { /* quota or blocked */ }
  }

  function restore() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          wishlistedHandles = new Set(parsed);
        }
      }
    } catch (e) {
      wishlistedHandles = new Set();
    }
  }

  function has(handle) {
    return wishlistedHandles.has(handle);
  }

  function count() {
    return wishlistedHandles.size;
  }

  /* --------------------------------------------------------------------------
     Pub/Sub integration
     -------------------------------------------------------------------------- */
  function publishEvent(action, handle) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.wishlistUpdate) {
      publish(PUB_SUB_EVENTS.wishlistUpdate, {
        action: action,
        handle: handle,
        count: count(),
        items: [...wishlistedHandles]
      });
    }
  }

  /* --------------------------------------------------------------------------
     UI Updates
     -------------------------------------------------------------------------- */
  function updateAllButtons() {
    document.querySelectorAll('[data-wishlist-handle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-wishlist-handle');
      if (handle) {
        var active = has(handle);
        btn.setAttribute('data-wishlist-active', active ? 'true' : 'false');
        btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
        btn.setAttribute('title', active ? 'Remove from wishlist' : 'Add to wishlist');
      }
    });
  }

  function updateCount() {
    var c = count();
    // Toggle header heart between empty/filled based on wishlist state
    document.querySelectorAll('.header-wishlist__icon--empty').forEach(function (el) {
      el.style.display = c > 0 ? 'none' : '';
    });
    document.querySelectorAll('[data-wishlist-header-filled]').forEach(function (el) {
      el.style.display = c > 0 ? '' : 'none';
    });
  }

  function animateButton(btn) {
    btn.classList.remove('wishlist-btn--animating');
    void btn.offsetWidth;
    btn.classList.add('wishlist-btn--animating');
  }

  /* --------------------------------------------------------------------------
     Gadget API — Fetch wishlists
     -------------------------------------------------------------------------- */

  /**
   * Fetch all wishlists for the current customer from Gadget.
   * Builds the handle → lookup map for add/remove operations.
   */
  async function fetchWishlistsFromAPI() {
    if (isFetching || !isApiReady || !api) return;
    if (!window.wishlistConfig?.customerLoggedIn) return;

    isFetching = true;

    try {
      // Fetch all wishlists with their items and variant product info
      console.log('[Wishlist] Fetching wishlists from Gadget API...');
      var wishlists = await api.wishlist.findMany({
        select: {
          id: true,
          name: true,
          wishlistItems: {
            edges: {
              node: {
                id: true,
                variant: {
                  id: true,
                  product: {
                    handle: true
                  }
                }
              }
            }
          }
        }
      });

      // Reset state
      var serverHandles = new Set();
      wishlistLookup = {};
      defaultWishlistId = null;

      if (wishlists && wishlists.length > 0) {
        // Use the first wishlist as the default for card-level add
        defaultWishlistId = wishlists[0].id;

        wishlists.forEach(function (wl) {
          var items = wl.wishlistItems?.edges || [];
          items.forEach(function (edge) {
            var item = edge.node;
            var handle = item.variant?.product?.handle;
            if (handle) {
              serverHandles.add(handle);
              // Store lookup for removal
              wishlistLookup[handle] = {
                wishlistId: wl.id,
                wishlistItemId: item.id,
                variantId: item.variant?.id
              };
            }
          });
        });
      }

      // Server is source of truth for logged-in users
      wishlistedHandles = serverHandles;
      persist();
      updateAllButtons();
      updateCount();
    } catch (e) {
      console.log('[Wishlist] API fetch failed, using localStorage cache', e);
    } finally {
      isFetching = false;
    }
  }

  /* --------------------------------------------------------------------------
     Gadget API — Add to wishlist
     -------------------------------------------------------------------------- */

  /**
   * Get the first available variant ID for a product handle.
   * Uses Shopify's AJAX Product API.
   */
  async function getFirstVariantId(handle) {
    try {
      var response = await fetch('/products/' + handle + '.json');
      var data = await response.json();
      var variants = data?.product?.variants || [];
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) {
          return String(variants[i].id);
        }
      }
      // Fallback to first variant if none available
      return variants.length > 0 ? String(variants[0].id) : null;
    } catch (e) {
      console.log('[Wishlist] Failed to fetch product variants', e);
      return null;
    }
  }

  /**
   * Add a product to the customer's default wishlist.
   * If no wishlist exists, creates one called "My Wishlist".
   */
  async function addToWishlistAPI(handle) {
    if (!isApiReady || !api) return;
    if (!window.wishlistConfig?.customerLoggedIn) return;

    try {
      var variantId = await getFirstVariantId(handle);
      if (!variantId) return;

      if (defaultWishlistId) {
        // Add to existing wishlist
        var item = await api.wishlistItem.create({
          wishlist: { _link: defaultWishlistId },
          variant: { _link: variantId }
        });

        if (item) {
          wishlistLookup[handle] = {
            wishlistId: defaultWishlistId,
            wishlistItemId: item.id,
            variantId: variantId
          };
        }
      } else {
        // Create a new wishlist with this item
        var wishlist = await api.wishlist.create({
          name: 'My Wishlist',
          wishlistItems: [{
            create: {
              variant: { _link: variantId }
            }
          }]
        });

        if (wishlist) {
          defaultWishlistId = wishlist.id;
          // Refresh to get the new wishlist item IDs
          await fetchWishlistsFromAPI();
        }
      }
    } catch (e) {
      console.log('[Wishlist] API add failed', e);
    }
  }

  /* --------------------------------------------------------------------------
     Gadget API — Remove from wishlist
     -------------------------------------------------------------------------- */

  /**
   * Remove a product from the customer's wishlist.
   */
  async function removeFromWishlistAPI(handle) {
    if (!isApiReady || !api) return;
    if (!window.wishlistConfig?.customerLoggedIn) return;

    try {
      var lookup = wishlistLookup[handle];
      if (lookup && lookup.wishlistItemId) {
        await api.wishlistItem.delete(lookup.wishlistItemId);
        delete wishlistLookup[handle];
      }
    } catch (e) {
      console.log('[Wishlist] API remove failed', e);
    }
  }

  /* --------------------------------------------------------------------------
     Toggle — Handles both localStorage and API
     -------------------------------------------------------------------------- */

  async function toggle(handle) {
    if (has(handle)) {
      // Remove
      wishlistedHandles.delete(handle);
      persist();
      updateAllButtons();
      updateCount();
      publishEvent('remove', handle);
      // Background API call
      removeFromWishlistAPI(handle);
    } else {
      // Add
      wishlistedHandles.add(handle);
      persist();
      updateAllButtons();
      updateCount();
      publishEvent('add', handle);
      // Background API call
      addToWishlistAPI(handle);
    }
  }

  /* --------------------------------------------------------------------------
     Event Delegation — Click handler for all wishlist buttons
     -------------------------------------------------------------------------- */
  function handleClick(event) {
    var btn = event.target.closest('.wishlist-btn');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    var handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;

    animateButton(btn);
    toggle(handle);
  }

  /* --------------------------------------------------------------------------
     Gadget Client — Load and initialize
     -------------------------------------------------------------------------- */

  function initGadgetClient() {
    if (typeof Gadget === 'undefined') {
      console.log('[Wishlist] Gadget client not loaded, using localStorage only');
      return;
    }

    try {
      api = new Gadget({ endpoint: GQL_ENDPOINT });
      isApiReady = true;
      console.log('[Wishlist] Gadget API client initialized');

      // Fetch fresh wishlist state from server
      fetchWishlistsFromAPI();
    } catch (e) {
      console.log('[Wishlist] Failed to initialize Gadget client', e);
    }
  }

  /* --------------------------------------------------------------------------
     Init
     -------------------------------------------------------------------------- */
  function init() {
    // Restore from localStorage (instant UI)
    restore();
    updateAllButtons();
    updateCount();

    // Event delegation for all wishlist buttons
    document.body.addEventListener('click', handleClick);

    // If customer is logged in, initialize Gadget API client
    if (window.wishlistConfig?.customerLoggedIn) {
      // Wait for the Gadget client script to load (it's loaded with defer)
      if (typeof Gadget !== 'undefined') {
        initGadgetClient();
      } else {
        // Poll briefly for the Gadget client to become available
        var attempts = 0;
        var checkInterval = setInterval(function () {
          attempts++;
          if (typeof Gadget !== 'undefined') {
            clearInterval(checkInterval);
            initGadgetClient();
          } else if (attempts > 20) {
            clearInterval(checkInterval);
            console.log('[Wishlist] Gadget client not available after timeout');
          }
        }, 250);
      }
    }

    // Listen for Gadget storefront extension events
    document.addEventListener('wishlist:changed', function () {
      if (isApiReady) fetchWishlistsFromAPI();
    });

    // MutationObserver for dynamically injected product cards
    var observer = new MutationObserver(function (mutations) {
      var hasNewButtons = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && (node.querySelector && node.querySelector('[data-wishlist-handle]') || node.hasAttribute && node.hasAttribute('data-wishlist-handle'))) {
            hasNewButtons = true;
          }
        });
      });
      if (hasNewButtons) updateAllButtons();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------------------------
     Expose globally for Gadget extension interop
     -------------------------------------------------------------------------- */
  window.__wishlist = {
    has: has,
    count: count,
    getItems: function () { return [...wishlistedHandles]; },
    refresh: fetchWishlistsFromAPI,
    toggle: toggle
  };

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
